import { useContext } from "react";
import { Divider, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatGregorianDate, formatPersianDate } from "../../../../utils/date";
import { LocaleContext } from "../../../../routing/LangRouter";

//import images and icons
import { Airplane } from "iconsax-react";

function IntFlightInformation({ intFlightDetails, segment, index }) {
    //general variables
    const { t } = useTranslation();
    const { locale } = useContext(LocaleContext);

    //format date in Persian format
    const formatDate = date => {
        return formatPersianDate(date, {
            day: "numeric",
            month: "long",
            year: undefined,
            weekday: "long",
        });
    };

    const airportName = segment?.destination?.terminal?.name;
    const hour = segment?.connection_time?.split(":")[0];
    const minutes = segment?.connection_time?.split(":")[1];

    return (
        <>
            <Grid className="int-flight-details-modal__details" container item xs={12} rowGap={3}>
                <Grid container item xs={12} md={7}>
                    <Grid
                        className="int-flight-details-modal__arrow"
                        container
                        item
                        xs={1}
                        direction="column"
                        justifyContent="space-between"
                        alignItems="center"
                    >
                        <Airplane color="var(--teal-color-18)" variant="Bold" />
                        <span className="int-flight-details-modal__dashed"></span>
                        <span className="int-flight-details-modal__point"></span>
                    </Grid>
                    <Grid
                        className="int-flight-details-modal__date"
                        container
                        item
                        xs={4.5}
                        direction="column"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        rowGap={{ xs: 5, md: 0 }}
                    >
                        <Grid container item xs="auto" direction="column">
                            <span>{segment?.departure_time.split(":", 2).join(":")}</span>
                            <span>
                                {locale === "fa"
                                    ? formatDate(segment?.departure_date)
                                    : formatGregorianDate(segment?.departure_date, null, "YYYY/MM/DD")}
                            </span>
                        </Grid>
                        <Grid container item xs="auto" direction="column">
                            <span>{segment?.destination_time?.split(":", 2).join(":")}</span>
                            <span>
                                {locale === "fa" ? formatDate(segment?.arrival_date) : formatGregorianDate(segment?.arrival_date, null, "YYYY/MM/DD")}
                            </span>
                        </Grid>
                    </Grid>
                    <Grid container item xs={6.5} rowGap={2} direction="column" justifyContent="space-between" alignItems="flex-start">
                        <Grid container item xs="auto" direction="column">
                            <span className="int-flight-details-modal__airport">
                                <span className="int-flight-details-modal__point-gray"></span>
                                {segment?.departure?.terminal?.name || segment?.domestic_departure}
                                {!!segment?.departure?.terminal?.code && <span>({segment?.departure?.terminal?.code})</span>}
                            </span>
                            {segment?.departure?.country?.persian && (
                                <span className="int-flight-details-modal__country">
                                    {locale === "en" ? segment?.departure?.country?.english : segment?.departure?.country?.persian} ,{" "}
                                    {locale === "en" ? segment?.departure?.city?.english : segment?.departure?.city?.persian}
                                </span>
                            )}
                        </Grid>
                        <Grid container item xs="auto" direction="column">
                            <span className="int-flight-details-modal__airport">
                                <span className="int-flight-details-modal__point-gray"></span>
                                {segment?.destination?.terminal?.name || segment?.domestic_destination}
                                {!!segment?.destination?.terminal?.code && <span>({segment?.destination?.terminal?.code})</span>}
                            </span>

                            {!!segment?.destination?.country?.persian && (
                                <span className="int-flight-details-modal__country">
                                    {locale === "en" ? segment?.destination?.country?.english : segment?.destination?.country?.persian} ,{" "}
                                    {locale === "en" ? segment?.destination?.city?.english : segment?.destination?.city?.persian}
                                </span>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid className="int-flight-details-modal__airline-details" container item xs={12} md={5} rowGap={{ xs: 0.5, md: 2 }}>
                    <Grid container item xs={12} rowGap={{ xs: 0, md: 1 }} direction="column">
                        <span className="int-flight-details-modal__title">{t("airline")}</span>
                        <span className="int-flight-details-modal__airline">
                            <img className="int-flight-details-modal__int-airline-logo" src={segment?.airline?.image} alt="airline" />
                            <Grid container item xs="auto" columnGap={1} justifyContent="space-between" alignItems="center">
                                <span className="int-flight-details-modal__airline-name">
                                    {locale === "en" ? segment?.airline?.english : segment?.airline?.persian}
                                </span>
                                {segment?.operating_airline?.english && (
                                    <span className="int-flight-details-modal__operating-airline">
                                        (Operating by {segment?.operating_airline?.english || " "})
                                    </span>
                                )}
                            </Grid>
                        </span>
                    </Grid>

                    <Divider width="100%" sx={{ borderBottomColor: "var(--light-gray-color)" }} />

                    <Grid className="int-flight-details-modal__information" container item xs={12} rowGap={1}>
                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("flightType")}</span>
                            <span className="int-flight-details-modal__name"></span>
                        </Grid>

                        <Grid container item xs={1} justifyContent="center">
                            <Divider orientation="vertical" flexItem sx={{ borderBottomColor: "var(--light-gray-color)" }} />
                        </Grid>

                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("flightNumber")}</span>
                            <span className="int-flight-details-modal__name">{segment?.flight_number}</span>
                        </Grid>

                        <Grid container item xs={1} justifyContent="center">
                            <Divider orientation="vertical" flexItem sx={{ borderBottomColor: "var(--light-gray-color)" }} />
                        </Grid>

                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("aircraft")}</span>
                            <span className="int-flight-details-modal__name">{segment?.aircraft || segment?.airline?.aircraft}</span>
                        </Grid>

                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("baggage")}</span>
                            <span className="int-flight-details-modal__name">{segment?.baggage}</span>
                        </Grid>

                        <Grid container item xs={1} justifyContent="center">
                            <Divider orientation="vertical" flexItem sx={{ borderBottomColor: "var(--light-gray-color)" }} />
                        </Grid>

                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("cabinClass")}</span>
                            <span className="int-flight-details-modal__name">
                                {locale === "en" ? intFlightDetails?.cabin?.english : intFlightDetails?.cabin?.persian || segment?.cobin_name}
                            </span>
                        </Grid>

                        <Grid container item xs={1} justifyContent="center">
                            <Divider orientation="vertical" flexItem sx={{ borderBottomColor: "var(--light-gray-color)" }} />
                        </Grid>

                        <Grid container item xs={3} direction="column" rowGap={1}>
                            <span className="int-flight-details-modal__title">{t("fareClass")}</span>
                            <span className="int-flight-details-modal__name">{intFlightDetails?.segments[0].fare_class}</span>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            <Grid
                className="int-flight-details-modal__stop"
                container
                item
                xs={12}
                display={index === intFlightDetails?.segments?.length - 1 ? "none" : "flex"}
            >
                {t("Messages.Layover Information", { airportName, hour, minutes })}
            </Grid>
        </>
    );
}

export default IntFlightInformation;
